import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { loadScript } from '@lib/core';
import { BehaviorSubject, Observable } from 'rxjs';

declare var ApplePaySession: any | undefined;

export interface ApplePayPaymentRequest {
  currencyCode: string;
  countryCode: string;
  supportedNetworks: string[];
  merchantCapabilities: string[];
  total: {
    label: string;
    amount: string;
  };
}
@Injectable({
  providedIn: 'root'
})
export class ApplePayService {
  private readonly APPLE_PAY_SCRIPT_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

  private isAvailable = new BehaviorSubject<boolean>(false);
  isAvailable$ = this.isAvailable.asObservable();

  private hasActiveCard = new BehaviorSubject<boolean>(false);
  hasActiveCard$ = this.hasActiveCard.asObservable();

  private isSelected = new BehaviorSubject<boolean>(false);
  isSelected$ = this.isSelected.asObservable();

  private scriptLoadAttempted = false;

  private merchantIdentifier = '';
  private currencyCode = 'PLN';
  private countryCode = 'PL';
  private supportedNetworks: Array<'visa' | 'masterCard' | 'amex' | 'mada' | 'cartesBancaires' | 'girocard' | 'eftpos' | 'elo' | 'mir' | 'rupay' | 'jcb'> = [
    'visa',
    'masterCard'
  ];
  private merchantCapabilities: Array<'supports3DS' | 'supportsCredit' | 'supportsDebit' | 'supportsEMV'> = ['supports3DS'];
  private resolvedVersion: number | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  init(merchantIdentifier: string, currencyCode?: string, countryCode?: string, supportedNetworks?: string[], merchantCapabilities?: string[]) {
    this.merchantIdentifier = merchantIdentifier;
    if (currencyCode) this.currencyCode = currencyCode;
    if (countryCode) this.countryCode = countryCode;
    if (supportedNetworks) this.supportedNetworks = supportedNetworks as any;
    if (merchantCapabilities) this.merchantCapabilities = merchantCapabilities as any;

    this.checkAvailability();
  }

  private get ApplePay(): any | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    if (typeof ApplePaySession !== 'undefined') {
      return ApplePaySession;
    }

    return undefined;
  }

  private resolveVersion(): number | null {
    const AP = this.ApplePay;
    if (!AP || typeof AP.supportsVersion !== 'function') return null;
    const supported = [15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].find((v) => AP.supportsVersion(v));
    return supported ?? null;
  }

  async checkAvailability(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      this.isAvailable.next(false);
      return false;
    }

    if (!this.ApplePay && !this.scriptLoadAttempted) {
      this.scriptLoadAttempted = true;
      try {
        await this.loadApplePaySDK();
        console.log('Apple Pay SDK loaded');
      } catch {
        this.isAvailable.next(false);
        return false;
      }
    }

    const AP = this.ApplePay;
    if (!AP) {
      this.isAvailable.next(false);
      return false;
    }

    this.resolvedVersion = this.resolveVersion();
    if (!this.resolvedVersion) {
      this.isAvailable.next(false);
      return false;
    }

    console.log('Apple Pay resolved version:', this.resolvedVersion);
    const canMakePayments = AP.canMakePayments();
    this.isAvailable.next(!!canMakePayments);

    if (canMakePayments && this.merchantIdentifier) {
      try {
        const active = await AP.canMakePaymentsWithActiveCard(this.merchantIdentifier);
        console.log('Active card for Apple Pay:', active);
        this.hasActiveCard.next(!!active);
      } catch {
        this.hasActiveCard.next(false);
      }
    } else {
      this.hasActiveCard.next(false);
    }

    console.log('Apple Pay availability checked:', !!canMakePayments);
    return !!canMakePayments;
  }

  public createApplePaySession(orderAmount: number, label?: string): any {
    if (!this.isAvailable.value) throw new Error('Apple Pay is not available');

    const AP = this.ApplePay;
    if (!AP || !this.resolvedVersion) throw new Error('Apple Pay not supported in this environment');

    const paymentRequest = {
      currencyCode: this.currencyCode,
      countryCode: this.countryCode,
      supportedNetworks: this.supportedNetworks,
      merchantCapabilities: this.merchantCapabilities,
      total: { label: label || 'Zamówienie', amount: orderAmount.toFixed(2) }
    };

    console.log('Creating Apple Pay Session with payment request:', paymentRequest);
    ApplePaySession = new AP(this.resolvedVersion, paymentRequest);
    return ApplePaySession;
  }

  public startPayment(onValidateMerchant: (event, session) => void, onPaymentAuthorized: (token) => void, onCancel: () => void): void {
    const session = ApplePaySession;

    session.onvalidatemerchant = (merchantEvent) => onValidateMerchant(merchantEvent, session);

    session.onpaymentauthorized = (paymentEvent) => {
      const completePaymentModel = {
        status: ApplePaySession.STATUS_SUCCESS
      };

      session.completePayment(completePaymentModel);
      const token: string | null = JSON.stringify(paymentEvent?.payment?.token?.paymentData) ?? null;
      onPaymentAuthorized(token);
    };

    session.oncancel = () => onCancel();

    session.begin();
  }

  private loadApplePaySDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Apple Pay script loading timeout')), 10000);
      loadScript(
        'applepay',
        this.APPLE_PAY_SCRIPT_URL,
        () => {
          clearTimeout(timeout);
          resolve();
        },
        true,
        false,
        true
      );
    });
  }
}
